import React from 'react';
import { Code2, Smartphone, Bot, TrendingUp, Cloud } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const supportingPoints = [
  {
    icon: Code2,
    text: 'SaaS platforms built for recurring revenue',
  },
  {
    icon: Smartphone,
    text: 'AI systems that streamline operations',
  },
  {
    icon: Bot,
    text: 'Automation that eliminates manual bottlenecks',
  },
  {
    icon: TrendingUp,
    text: 'Growth engines that scale customer acquisition',
  },
  {
    icon: Cloud,
    text: 'Infrastructure that scales from startup to enterprise',
  },
];

export const AboutSection = () => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="about"
      ref={sectionRef}
      data-testid="about-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Editorial Layout: Two Column on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            {/* Left Column: Text Content */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              {/* Section Label */}
              <p
                className="kv-reveal text-xs font-semibold uppercase tracking-[0.2em] mb-3"
                style={{ color: '#0A3D91' }}
                data-testid="about-label"
              >
                About Us
              </p>

              {/* Headline */}
              <h2
                className="kv-reveal text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
                data-testid="about-headline"
              >
                The Narrative
              </h2>

              {/* Primary Content */}
              <div className="kv-reveal space-y-4" data-testid="about-content">
                <p
                  className="text-base sm:text-lg"
                  style={{ color: '#475569', lineHeight: '1.7' }}
                >
                  At{' '}
                  <span className="font-semibold" style={{ color: '#131B2E' }}>
                    KritvaSoft
                  </span>
                  , we don't just build software — we engineer{' '}
                  <span style={{ color: '#0A3D91' }} className="font-semibold">
                    revenue-generating systems
                  </span>{' '}
                  that transform how ambitious companies operate and scale. Our systems are 
                  engineered for{' '}
                  <span className="font-semibold" style={{ color: '#131B2E' }}>
                    maximum operational efficiency
                  </span>{' '}
                  from day one.
                </p>

                <p
                  className="text-base sm:text-lg leading-relaxed"
                  style={{ color: '#475569' }}
                >
                  We specialize in{' '}
                  <span style={{ color: '#0A3D91' }} className="font-semibold">
                    SaaS platforms that generate recurring revenue
                  </span>
                  ,{' '}
                  <span className="font-semibold" style={{ color: '#131B2E' }}>
                    AI agents that eliminate manual bottlenecks
                  </span>
                  , and{' '}
                  <span className="font-semibold" style={{ color: '#131B2E' }}>
                    growth engines that scale customer acquisition
                  </span>
                  .
                </p>

                <p
                  className="text-base sm:text-lg leading-relaxed"
                  style={{ color: '#475569' }}
                >
                  Unlike agencies that deliver projects and disappear, KritvaSoft operates as a{' '}
                  <span style={{ color: '#0A3D91' }} className="font-semibold">
                    strategic technology partner
                  </span>{' '}
                  — invested in your outcomes, accountable for results, and committed to 
                  long-term growth.
                </p>
              </div>

              {/* Divider */}
              <div
                className="my-8 w-16 h-1 rounded-full"
                style={{ backgroundColor: '#00BFFF' }}
              />

              {/* Supporting Points */}
              <div
                className="space-y-3"
                data-testid="about-supporting-points"
              >
                {supportingPoints.map((point, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 group"
                    data-testid={`about-point-${index + 1}`}
                  >
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200"
                      style={{ 
                        backgroundColor: 'rgba(10, 61, 145, 0.05)',
                      }}
                    >
                      <point.icon
                        className="w-4 h-4"
                        style={{ color: '#0A3D91' }}
                      />
                    </div>
                    <p
                      className="text-sm pt-2"
                      style={{ color: '#475569' }}
                    >
                      {point.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Visual Element */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="sticky top-32">
                {/* Premium Visual Block */}
                <div
                  className="relative rounded-2xl overflow-hidden"
                  style={{ backgroundColor: 'rgba(10, 61, 145, 0.02)' }}
                  data-testid="about-visual"
                >
                  {/* Abstract Pattern */}
                  <div className="absolute inset-0 opacity-[0.03]">
                    <div
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `
                          radial-gradient(circle at 20% 30%, #0A3D91 1px, transparent 1px),
                          radial-gradient(circle at 80% 70%, #00BFFF 1px, transparent 1px)
                        `,
                        backgroundSize: '40px 40px',
                      }}
                    />
                  </div>

                  {/* Content Card */}
                  <div className="relative p-6 sm:p-8">
                    {/* Decorative Elements */}
                    <div
                      className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10"
                      style={{ 
                        background: 'radial-gradient(circle, #00BFFF 0%, transparent 70%)',
                        transform: 'translate(30%, -30%)'
                      }}
                    />
                    <div
                      className="absolute bottom-0 left-0 w-20 h-20 rounded-full opacity-10"
                      style={{ 
                        background: 'radial-gradient(circle, #0A3D91 0%, transparent 70%)',
                        transform: 'translate(-30%, 30%)'
                      }}
                    />

                    {/* Quote/Highlight Block */}
                    <div className="relative">
                      <div
                        className="text-5xl font-serif leading-none mb-3 opacity-20"
                        style={{ color: '#0A3D91' }}
                      >
                        "
                      </div>
                      <p
                        className="text-lg sm:text-xl font-semibold leading-snug mb-4"
                        style={{ color: '#131B2E' }}
                      >
                        Your Strategic Growth Partner
                      </p>
                      <p
                        className="text-sm leading-relaxed mb-6"
                        style={{ color: '#475569' }}
                      >
                        We're not a vendor. We're invested in your success — combining 
                        world-class engineering with business strategy to deliver 
                        measurable outcomes.
                      </p>

                      {/* Stats Row */}
                      <div
                        className="grid grid-cols-2 gap-4 pt-4 border-t"
                        style={{ borderColor: '#E2E8F0' }}
                      >
                        <div>
                          <p
                            className="text-2xl font-bold"
                            style={{ color: '#0A3D91' }}
                          >
                            100x
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: '#475569' }}
                          >
                            Growth-Ready Architecture
                          </p>
                        </div>
                        <div>
                          <p
                            className="text-2xl font-bold"
                            style={{ color: '#0A3D91' }}
                          >
                            99.9%
                          </p>
                          <p
                            className="text-xs mt-1"
                            style={{ color: '#475569' }}
                          >
                            Uptime SLA
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
